/**
 * Aruba RDV (Reception Device Validation) store — queue of received devices
 * waiting to be staged into Aruba Central.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";
import {
  ArubaRdvDeviceListSchema,
  type ArubaRdvDevice,
} from "@/api/schemas/aruba";
import { MOCK_ARUBA_RDV } from "@/api/fixtures/aruba-rdv";

interface State {
  data: ArubaRdvDevice[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  load: () => Promise<void>;
  /** Re-run validation for a single device (advances queued/failed → validating). */
  rerun: (serial: string) => Promise<void>;
  /** Approve / move to a target site once validation passes. */
  approve: (serial: string, site: string) => Promise<void>;
}

export const useArubaRdv = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/aruba/rdv/");
      set({ data: ArubaRdvDeviceListSchema.parse(raw), status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_ARUBA_RDV,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  rerun: async (serial) => {
    // Optimistic — flip to validating immediately so the UI feels live.
    set((s) => ({
      data: s.data.map((d) => (d.serial === serial ? { ...d, state: "validating" } : d)),
    }));
    try {
      await apiPost(`/aruba/rdv/${serial}/rerun/`);
    } catch {
      // ignored — the next load() will reconcile.
    }
    void get().load();
  },

  approve: async (serial, site) => {
    set((s) => ({
      data: s.data.map((d) =>
        d.serial === serial ? { ...d, state: "ok", targetSite: site } : d,
      ),
    }));
    try {
      await apiPost(`/aruba/rdv/${serial}/approve/`, { site });
    } catch {
      // ignored — next load() will reconcile.
    }
    void get().load();
  },
}));
